class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains("hidden");
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = "";
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define("search-form", SearchForm);

class DynamicSearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector(".js-search-input");
    this.resultsContainer = this.querySelector(".js-search-results");
    this.overlay = this.querySelector(".js-search-overlay");
    this.resetBtn = this.querySelector(".js-search-reset");
    this.init();
    this.addListeners();
  }

  init() {
    if (
      this.input.dataset.placeholderTwo ||
      this.input.dataset.placeholderThree
    ) {
      this.typeInPlaceholders();
    }
  }

  addListeners() {
    this.input.addEventListener(
      "input",
      debounce(this.handleInput.bind(this), 200)
    );
    this.resetBtn.addEventListener("click", this.handleReset.bind(this));
    this.overlay.addEventListener("click", this.handleReset.bind(this));
  }

  async handleInput() {
    const query = this.input.value.trim();
    if (query.length < 1) {
      this.resultsContainer.classList.add("hidden");
      this.resultsContainer.innerHTML = "";
      this.overlay.classList.remove("is-visible");

      return;
    }

    try {
      const results = await this.fetchResults(query);
      if (results.resources.results.products.length > 0) {
        this.displayResults(results);
        this.resultsContainer.classList.remove("hidden"); // Ensure results are shown
      } else {
        this.resultsContainer.innerHTML =
          '<p class="no-result-found">No results found</p>';
        this.resultsContainer.classList.remove("hidden");
        this.overlay.classList.add("is-visible");
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  }

  async fetchResults(query) {
    const endpoint = `/search/suggest.json?q=${encodeURIComponent(
      query
    )}&resources[type]=product,collection`;
    const response = await fetch(endpoint);
    if (!response.ok) throw new Error("Failed to fetch search results");
    return response.json();
  }

  displayResults(data) {
    this.overlay.classList.add("is-visible");
    document.body.classList.add("overlay-predictive-search");

    const products = data.resources.results.products || [];
    const collections = data.resources.results.collections || [];

    let resultsHTML = "";
    if (products.length > 0) {
      resultsHTML += `
        <div class="predictive-search__section">
          <h3 class="predictive-search__title">Products</h3>
          <ul class="predictive-search__list">
            ${products
              .map(
                (product) => `
              <li>
                <a href="${product.url}" class="predictive-result flex items-start">
                  <img src="${product.featured_image.url}" alt="${product.title}" class="result-thumbnail" />
                  <div class="result-details">
                    <h4>${product.title}</h4>
                    <p>${product.price}</p>
                  </div>
                </a>
              </li>
            `
              )
              .join("")}
          </ul>
        </div>
      `;
    }
    if (collections.length > 0) {
      resultsHTML += `
        <div class="predictive-search__section">
          <h3 class="predictive-search__title">Collections</h3>
          <ul class="predictive-search__list">
            ${collections
              .map(
                (collection) => `
              <li>
                <a href="${collection.url}" class="predictive-result flex items-start">
                  <img src="${collection.featured_image.url}" alt="${collection.title}" class="result-thumbnail" />
                  <div class="result-details">
                    <h4>${collection.title}</h4>
                  </div>
                </a>
              </li>
            `
              )
              .join("")}
          </ul>
        </div>
      `;
    }

    if (!resultsHTML) {
      this.resultsContainer.innerHTML =
        '<p class="no-result-found">No results found</p>';
      this.resultsContainer.classList.remove("hidden");
      return;
    }

    this.resultsContainer.innerHTML = `
      <div class="predictive-search__container">
        ${resultsHTML}
      </div>
    `;
    this.resultsContainer.classList.remove("hidden");
  }

  handleReset() {
    this.input.value = "";
    this.resultsContainer.innerHTML = "";
    this.resultsContainer.classList.add("hidden");

    this.overlay.classList.remove("is-visible");
    document.body.classList.remove("overlay-predictive-search");
  }

  typeInPlaceholders() {
    const typingSpeed = parseInt(this.input.dataset.typingSpeed || 100, 10);
    const deletingSpeed = parseInt(this.input.dataset.deletingSpeed || 60, 10);
    const delayAfterDeleting = parseInt(
      this.input.dataset.delayAfterDeleting || 500,
      10
    );
    const delayBeforeFirstDelete = parseInt(
      this.input.dataset.delayBeforeFirstDelete || 2000,
      10
    );
    const delayAfterWordTyped = parseInt(
      this.input.dataset.delayAfterWordTyped || 2400,
      10
    );

    const placeholders = [
      this.input.dataset.placeholderOne || "",
      this.input.dataset.placeholderTwo || "",
      this.input.dataset.placeholderThree || "",
    ].filter(Boolean);

    let index = 0;

    const typeNextPlaceholder = async () => {
      const placeholder = placeholders[index];
      await this.type(placeholder, typingSpeed);
      await this.wait(delayAfterWordTyped);
      await this.delete(placeholder, deletingSpeed);
      await this.wait(delayAfterDeleting);

      index = (index + 1) % placeholders.length;
      typeNextPlaceholder(); // Recursive call to continue animation
    };

    this.wait(delayBeforeFirstDelete).then(typeNextPlaceholder);
  }

  type(text, speed) {
    return new Promise((resolve) => {
      let currentText = "";
      let i = 0;
      const interval = setInterval(() => {
        currentText += text[i];
        this.input.placeholder = currentText;
        i++;
        if (i >= text.length) {
          clearInterval(interval);
          resolve();
        }
      }, speed);
    });
  }

  delete(text, speed) {
    return new Promise((resolve) => {
      let i = text.length;
      const interval = setInterval(() => {
        this.input.placeholder = text.slice(0, i);
        i--;
        if (i < 0) {
          clearInterval(interval);
          resolve();
        }
      }, speed);
    });
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

customElements.define("dynamic-search-form", DynamicSearchForm);
